if (!customElements.get("pickup-availability")) {
    customElements.define(
        "pickup-availability",
        class PickupAvailability extends HTMLElement {
            constructor() {
                super();
                this.lowStockColor = "#FF9800";

                if (!this.hasAttribute("available")) {
                    return;
                }

                this.templates = {
                    unavailable: this.querySelector('template[data-template="unavailable"]'),
                    available: this.querySelector('template[data-template="available"]'),
                    drawer: this.querySelector('template[data-template="drawer"]'),
                    locationItem: this.querySelector('template[data-template="location-item"]'),
                };

                // Get internationalized strings from data attributes
                this.i18n = {
                    availableForPickup: this.dataset.i18nAvailableForPickup,
                    notAvailableAnyLocation: this.dataset.i18nNotAvailableAnyLocation,
                    inStock: this.dataset.i18nInStock,
                    inStockAt: this.dataset.i18nInStockAt,
                    inStockAtLowStock: this.dataset.i18nInStockAtLowStock,
                    andOtherLocations: this.dataset.i18nAndOtherLocations,
                    andOtherLocationsPlural: this.dataset.i18nAndOtherLocationsPlural,
                    viewStoreAvailability: this.dataset.i18nViewStoreAvailability,
                };

                this.onClickRefreshList = this.onClickRefreshList.bind(this);
                this.onClickViewPickupAvailability = this.onClickViewPickupAvailability.bind(this);
                this.onVariantChange = this.onVariantChange.bind(this);

                // Listen for variant change events as a fallback
                document.addEventListener("product:variant-change", this.onVariantChange);

                this.fetchAvailability(this.dataset.variantId, this.dataset.variantSku);
            }

            fetchAvailability(variantId, variantSku) {
                // Handle backwards compatibility - if only variantId is passed, get SKU from data attribute
                if (!variantSku && variantId) {
                    variantSku = this.dataset.variantSku;
                }

                if (!variantId || !variantSku) return;

                // Update the component's data attributes with current variant info
                this.dataset.variantId = variantId;
                this.dataset.variantSku = variantSku;

                let rootUrl = this.dataset.rootUrl;
                if (!rootUrl.endsWith("/")) {
                    rootUrl = rootUrl + "/";
                }

                const locationsStockUrl = `${rootUrl}${window.vitapur.locations_stock_url}`;

                fetch(locationsStockUrl)
                    .then((response) => response.json())
                    .then((data) => {
                        this.renderCustomAvailability(data, variantSku);
                    })
                    .catch((e) => {
                        console.error("Error fetching stock data:", e);
                        this.renderError();
                    });
            }

            onVariantChange(event) {
                // Fallback event listener for variant changes
                if (event.detail && event.detail.variant) {
                    const variant = event.detail.variant;
                    if (variant.id && variant.sku) {
                        this.fetchAvailability(variant.id, variant.sku);
                    }
                }
            }

            renderCustomAvailability(locationsData, variantSku) {
                // Remove any existing drawer
                const existingDrawer = document.querySelector("pickup-availability-drawer");
                if (existingDrawer) {
                    existingDrawer.remove();
                }

                // Find locations that have stock for this variant SKU
                const availableLocations = locationsData.filter((location) => {
                    return location.stock && location.stock[variantSku] && location.stock[variantSku] > 0;
                });

                if (availableLocations.length === 0) {
                    this.renderNoAvailability();
                    return;
                }

                this.renderPreviewWithStock(availableLocations, variantSku);

                this.createCustomDrawer(availableLocations, variantSku);
            }

            renderPreviewWithStock(availableLocations, variantSku) {
                if (!this.templates.available) return;

                const sortedLocations = availableLocations.sort((a, b) => a.click_and_collect ? -1 : 1);
                const totalLocations = sortedLocations.length;
                const firstLocation = sortedLocations[0];
                const stock = firstLocation.stock[variantSku];
                const stockThreshold = window.vitapur?.stock_settings?.only_x_left || 3;

                const previewElement = this.templates.available.content.cloneNode(true);

                const isLowStock = stock <= stockThreshold;
                const stockStatusClass = isLowStock ? "pickup-availability-warning" : "pickup-availability-success";

                const previewContainer = previewElement.querySelector(".pickup-availability-preview");
                if (previewContainer) {
                    previewContainer.classList.add(stockStatusClass);
                }

                if (previewContainer && !firstLocation.click_and_collect) {
                    previewContainer.classList.add("hidden");
                }

                const stockInfo = previewElement.querySelector("[data-stock-info]");
                if (stockInfo) {
                    let stockText;

                    if (isLowStock) {
                        stockText = this.i18n.inStockAtLowStock.replace("[COUNT]", stock).replace("[LOCATION]", firstLocation.name);
                    } else {
                        stockText = this.i18n.inStockAt.replace("[LOCATION]", firstLocation.name);
                    }

                    if (totalLocations > 1) {
                        const otherCount = totalLocations - 1;
                        const otherText =
                            otherCount === 1
                                ? this.i18n.andOtherLocations.replace("[COUNT]", otherCount)
                                : this.i18n.andOtherLocationsPlural.replace("[COUNT]", otherCount);
                        stockText += ` ${otherText}`;
                    }

                    stockInfo.textContent = stockText;
                    stockInfo.style.color = isLowStock ? this.lowStockColor : "#404040";
                }

                this.innerHTML = "";
                this.appendChild(previewElement);
                this.setAttribute("available", "");

                const button = this.querySelector(".pickup-availability-button");
                if (button) {
                    button.addEventListener("click", this.onClickViewPickupAvailability);
                }
            }

            renderNoAvailability() {
                if (!this.templates.unavailable) return;

                const previewElement = this.templates.unavailable.content.cloneNode(true);

                const subtitle = previewElement.querySelector(".pickup-availability-info p:last-of-type");
                if (subtitle && this.i18n.notAvailableAnyLocation) {
                    subtitle.textContent = this.i18n.notAvailableAnyLocation;
                }

                this.innerHTML = "";
                this.appendChild(previewElement);
                this.removeAttribute("available");

                const button = this.querySelector(".pickup-availability-button");
                if (button) {
                    button.addEventListener("click", this.onClickRefreshList);
                }
            }

            createCustomDrawer(availableLocations, variantSku) {
                if (!this.templates.drawer || !this.templates.locationItem) return;

                const drawer = this.templates.drawer.content.cloneNode(true);
                const locationsContainer = drawer.querySelector("[data-locations-container]");
                const stockThreshold = window.vitapur?.stock_settings?.only_x_left || 3;

                if (locationsContainer) {
                    availableLocations.forEach((location) => {
                        const stock = location.stock[variantSku];
                        const address = location.address;
                        const formattedAddress = address.formatted
                            ? address.formatted.join(", ")
                            : [address.address1, address.city, address.country].filter(Boolean).join(", ");

                        const locationElement = this.templates.locationItem.content.cloneNode(true);
                        const isLowStock = stock <= stockThreshold;

                        const nameElement = locationElement.querySelector("[data-location-name]");
                        if (nameElement) nameElement.textContent = location.name;

                        const stockElement = locationElement.querySelector("[data-stock-count]");
                        if (stockElement) {
                            if (isLowStock) {
                                stockElement.textContent = this.i18n.inStockAtLowStock.replace("[COUNT]", stock).replace("[LOCATION]", location.name);
                                stockElement.style.backgroundColor = this.lowStockColor;
                                stockElement.style.color = "#ffffff";
                            } else {
                                stockElement.textContent = this.i18n.inStock;
                                stockElement.style.backgroundColor = "var(--color-success-background)";
                                stockElement.style.color = "var(--color-success)";
                            }
                        }

                        const addressElement = locationElement.querySelector("[data-location-address]");
                        if (addressElement) addressElement.textContent = formattedAddress;

                        const phoneElement = locationElement.querySelector("[data-location-phone]");
                        if (phoneElement && address.phone) {
                            phoneElement.textContent = address.phone;
                            phoneElement.style.display = "block";
                        }

                        if (isLowStock) {
                            const itemContainer = locationElement.querySelector(".pickup-availability-item");
                            if (itemContainer) {
                                itemContainer.style.borderLeft = `3px solid ${this.lowStockColor}`;
                                itemContainer.style.paddingLeft = "1.5rem";
                            }
                        }

                        locationsContainer.appendChild(locationElement);
                    });
                }

                document.body.appendChild(drawer);
            }

            onClickViewPickupAvailability(evt) {
                evt.preventDefault();
                const drawer = document.querySelector("pickup-availability-drawer");
                if (drawer && typeof drawer.open === "function") {
                    drawer.open();
                }
            }

            onClickRefreshList(evt) {
                evt.preventDefault();
                this.fetchAvailability(this.dataset.variantId, this.dataset.variantSku);
            }

            renderError() {
                this.renderNoAvailability();
            }

            disconnectedCallback() {
                // Clean up event listener when component is removed
                document.removeEventListener("product:variant-change", this.onVariantChange);
            }
        }
    );
}

if (!customElements.get("pickup-availability-drawer")) {
    class PickupAvailabilityDrawer extends HTMLElement {
        constructor() {
            super();
            this.addEventListener("click", this.onClick.bind(this));
            this.onBodyClick = this.onBodyClick.bind(this);
        }

        onClick(event) {
            if (
                event.target.classList.contains("drawer-close") ||
                event.target.classList.contains("modal-dialog-close") ||
                event.target.closest(".drawer-close")
            ) {
                this.close();
            }

            // Close if clicking outside the drawer content
            if (event.target === this) {
                this.close();
            }
        }

        onBodyClick(event) {
            // Close drawer if clicking outside the drawer content
            if (!this.contains(event.target)) {
                this.close();
            }
        }

        open() {
            this.setAttribute("open", "");
            document.body.classList.add("overflow-hidden", "drawer-open");

            // Add body click listener to close drawer when clicking outside
            setTimeout(() => {
                document.body.addEventListener("click", this.onBodyClick);
            }, 100);

            // Focus management
            const firstFocusable = this.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
            if (firstFocusable) {
                firstFocusable.focus();
            }
        }

        close() {
            this.removeAttribute("open");
            document.body.classList.remove("overflow-hidden", "drawer-open");

            // Remove body click listener
            document.body.removeEventListener("click", this.onBodyClick);
        }

        connectedCallback() {
            // Handle escape key
            this.addEventListener("keydown", (event) => {
                if (event.key === "Escape") {
                    this.close();
                }
            });
        }
    }

    customElements.define("pickup-availability-drawer", PickupAvailabilityDrawer);
}

document.querySelectorAll("pickup-availability").forEach((el) => {
    if (typeof el.connectedCallback === "function") {
        el.connectedCallback();
    }
});
